import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bot,
  Home,
  Inbox,
  LayoutDashboard,
  LogOut,
  Menu,
  Radar,
  RefreshCw,
  Send,
  Settings,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { TacitLogo } from '@/components/ui/Logo';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useLinkedInDraftCounts } from '@/hooks/useLinkedInDrafts';

interface NavigationItem {
  name: string;
  href: string;
  icon: LucideIcon;
  exact?: boolean;
  showBadge?: boolean;
  adminOnly?: boolean;
}

interface NavigationGroup {
  key: string;
  label: string;
  items: NavigationItem[];
}

// Daily: the working surfaces used every day.
const primaryNavigation: NavigationItem[] = [
  { name: 'Mission Control', href: '/', icon: Home, exact: true },
  { name: 'Inbox', href: '/inbox', icon: Inbox, showBadge: true },
  { name: 'CRM', href: '/crm', icon: LayoutDashboard },
  { name: 'AI Sales Agent', href: '/ai-prodavac', icon: Bot },
];

// Admin: operational surfaces, hidden entirely for non-admins.
const adminNavigation: NavigationItem[] = [
  { name: 'Lead Inbox', href: '/lead-inbox', icon: UserPlus, adminOnly: true },
  { name: 'Outreach', href: '/outreach', icon: Send, adminOnly: true },
  { name: 'Marketing Sync', href: '/marketing-sync', icon: RefreshCw, adminOnly: true },
  { name: 'Intent Signals', href: '/high-intent', icon: Radar, adminOnly: true },
];

// Account: settings and identity-scoped surfaces.
const accountNavigation: NavigationItem[] = [
  { name: 'Settings', href: '/settings', icon: Settings },
];

const isItemActive = (pathname: string, item: NavigationItem): boolean =>
  item.exact
    ? pathname === item.href
    : pathname === item.href || pathname.startsWith(`${item.href}/`);

function PendingBadge({
  count,
  compact = false,
}: {
  count: number;
  compact?: boolean;
}): JSX.Element | null {
  if (count <= 0) {
    return null;
  }

  return (
    <span
      aria-label={`${count} pending drafts`}
      className={cn(
        'flex items-center justify-center rounded-full bg-amber-500 font-bold text-background',
        compact
          ? 'absolute right-2 top-1 h-4 min-w-4 px-1 text-[9px]'
          : 'h-5 min-w-5 px-1.5 text-[10px]'
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function Sidebar(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isAdmin, role } = useAuth();
  const { toast } = useToast();
  const [moreOpen, setMoreOpen] = useState(false);
  const draftCounts = useLinkedInDraftCounts();
  const pendingCount = draftCounts.data?.pending_approval ?? 0;

  const visibleAdminNavigation = adminNavigation.filter(
    (item) => !item.adminOnly || isAdmin
  );
  const visibleSecondaryNavigation = [...visibleAdminNavigation, ...accountNavigation];
  const navigationGroups: NavigationGroup[] = [
    { key: 'daily', label: 'Daily', items: primaryNavigation },
    ...(visibleAdminNavigation.length > 0
      ? [{ key: 'admin', label: 'Admin', items: visibleAdminNavigation }]
      : []),
    { key: 'account', label: 'Account', items: accountNavigation },
  ];
  const secondaryNavigationGroups = navigationGroups.filter((group) => group.key !== 'daily');
  const activeSecondaryItem = visibleSecondaryNavigation.find((item) =>
    isItemActive(location.pathname, item)
  );

  const handleSignOut = async (): Promise<void> => {
    setMoreOpen(false);
    await signOut();
    toast({
      title: 'Signed out',
      description: 'You have been signed out successfully.',
    });
    navigate('/login');
  };

  return (
    <>
      <header className="flex h-14 shrink-0 items-center border-b border-border bg-card px-4 shadow-elevation-1 md:hidden">
        <Link
          to="/"
          className="flex min-h-11 min-w-0 items-center gap-2 rounded-md"
          aria-label="Tacit, Mission Control"
        >
          <TacitLogo className="h-8 w-8 shrink-0" />
          <span className="min-w-0">
            <span className="block truncate text-base font-bold tracking-tight">Tacit</span>
            <span className="block truncate text-[11px] text-muted-foreground">
              Strategic Context Engine
            </span>
          </span>
        </Link>
      </header>

      <aside
        aria-label="Primary navigation"
        className="hidden h-full w-[72px] shrink-0 flex-col border-r border-border bg-card shadow-elevation-2 md:flex lg:hidden"
      >
        <Link
          to="/"
          className="m-3 flex h-12 items-center justify-center rounded-lg hover:bg-surface-hover"
          aria-label="Tacit, Mission Control"
        >
          <TacitLogo className="h-9 w-9" />
        </Link>
        <Separator />
        <nav className="flex flex-1 flex-col items-center gap-1 overflow-y-auto px-2 py-3">
          {navigationGroups.map((group, groupIndex) => (
            <div key={group.key} className="flex w-full flex-col items-center gap-1">
              {groupIndex > 0 && <Separator className="my-1 w-8" />}
              {group.items.map((item) => {
                const active = isItemActive(location.pathname, item);
                const Icon = item.icon;

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    aria-current={active ? 'page' : undefined}
                    aria-label={item.name}
                    title={item.name}
                    className={cn(
                      'interactive relative flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors',
                      active
                        ? 'bg-surface-hover text-foreground shadow-elevation-1'
                        : 'text-muted-foreground hover:bg-surface-hover hover:text-foreground'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {item.showBadge && <PendingBadge count={pendingCount} compact />}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <Separator />
        <Button
          variant="ghost"
          size="icon"
          className="m-3 h-11 w-11 shrink-0"
          onClick={handleSignOut}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="h-5 w-5" />
        </Button>
      </aside>

      <aside
        aria-label="Primary navigation"
        className="hidden h-full w-64 shrink-0 flex-col border-r border-border bg-card shadow-elevation-2 lg:flex"
      >
        <div className="p-6">
          <Link
            to="/"
            className="flex items-center gap-3 rounded-lg"
            aria-label="Tacit, Mission Control"
          >
            <TacitLogo className="h-10 w-10" />
            <span>
              <span className="block text-2xl font-bold tracking-tight">Tacit</span>
              <span className="block text-sm text-muted-foreground">
                Strategic Context Engine
              </span>
            </span>
          </Link>
        </div>
        <Separator />
        <nav className="flex-1 overflow-y-auto p-4">
          {navigationGroups.map((group, groupIndex) => (
            <div key={group.key} className={groupIndex > 0 ? 'mt-4' : undefined}>
              {groupIndex > 0 && <Separator className="mb-4" />}
              <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {group.label}
              </p>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const active = isItemActive(location.pathname, item);
                  const Icon = item.icon;

                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'interactive flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        active
                          ? 'bg-surface-hover text-foreground shadow-elevation-1'
                          : 'text-muted-foreground hover:bg-surface-hover hover:text-foreground'
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="flex-1">{item.name}</span>
                      {item.showBadge && <PendingBadge count={pendingCount} />}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <Separator />
        <div className="p-4">
          <div className="flex items-center gap-3 rounded-lg border border-border bg-surface p-3 shadow-elevation-1">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {user?.email?.split('@')[0] || 'User'}
              </p>
              <p className="text-xs capitalize text-muted-foreground">{role ?? 'member'}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={handleSignOut}
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      <nav
        aria-label="Mobile navigation"
        className="fixed inset-x-0 bottom-0 z-40 grid h-[calc(4rem+env(safe-area-inset-bottom))] grid-cols-5 border-t border-border bg-card/95 px-1 pb-[env(safe-area-inset-bottom)] shadow-elevation-4 backdrop-blur md:hidden"
      >
        {primaryNavigation.map((item) => {
          const active = isItemActive(location.pathname, item);
          const Icon = item.icon;

          return (
            <Link
              key={item.href}
              to={item.href}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'interactive relative flex min-h-11 min-w-11 flex-col items-center justify-center gap-0.5 rounded-lg px-1 text-[10px] font-medium transition-colors',
                active
                  ? 'text-primary'
                  : 'text-muted-foreground hover:bg-surface-hover hover:text-foreground'
              )}
            >
              <Icon className="h-5 w-5" />
              <span className="max-w-full truncate">{item.name}</span>
              {item.showBadge && <PendingBadge count={pendingCount} compact />}
            </Link>
          );
        })}

        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              data-testid="mobile-more-trigger"
              aria-current={activeSecondaryItem ? 'page' : undefined}
              aria-label={
                activeSecondaryItem
                  ? `More, current section: ${activeSecondaryItem.name}`
                  : 'More navigation'
              }
              aria-expanded={moreOpen}
              className={cn(
                'interactive flex min-h-11 min-w-11 flex-col items-center justify-center gap-0.5 rounded-lg px-1 text-[10px] font-medium transition-colors',
                activeSecondaryItem
                  ? 'text-primary'
                  : 'text-muted-foreground hover:bg-surface-hover hover:text-foreground'
              )}
            >
              <Menu className="h-5 w-5" />
              <span className="max-w-full truncate">
                {activeSecondaryItem?.name ?? 'More'}
              </span>
            </button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="max-h-[85dvh] rounded-t-2xl border-border pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
          >
            <SheetHeader className="pr-12 text-left">
              <SheetTitle>More</SheetTitle>
              <SheetDescription>Additional Tacit sections and account actions.</SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-4">
              {secondaryNavigationGroups.map((group, groupIndex) => (
                <div key={group.key}>
                  {groupIndex > 0 && <Separator className="mb-4" />}
                  <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {group.label}
                  </p>
                  <div className="space-y-2">
                    {group.items.map((item) => {
                      const active = isItemActive(location.pathname, item);
                      const Icon = item.icon;

                      return (
                        <Button
                          key={item.href}
                          asChild
                          variant={active ? 'secondary' : 'ghost'}
                          className="h-12 w-full justify-start px-3 text-base"
                        >
                          <Link
                            to={item.href}
                            aria-current={active ? 'page' : undefined}
                            onClick={() => setMoreOpen(false)}
                          >
                            <Icon className="h-5 w-5" />
                            {item.name}
                          </Link>
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <Separator className="my-4" />
            <Button
              type="button"
              variant="ghost"
              className="h-12 w-full justify-start px-3 text-base text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={handleSignOut}
            >
              <LogOut className="h-5 w-5" />
              Sign out
            </Button>
          </SheetContent>
        </Sheet>
      </nav>
    </>
  );
}
